import { normalizeColorHex } from '@/lib/utils';
import { OpsiumUserShortResponse } from '@/modules/common';
import {
  mapAttributeValueFromResponse,
  mapCustomAttributesToDTO,
} from '@/modules/common/mappers/custom-attribute.mapper';
import { CustomAttribute } from '@/modules/common/types/custom-attribute';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { ProjectFormData } from '../types';
import { ProjectInfoResponse } from '../types/dto/project-info.dto';
import { CreateProjectInfoChangeRequest } from '../types/dto/request/create-project-request';
import { Manager, ProjectInfo, ProjectStatus } from '../types/project';
import { mapProjectStatusFromDTO, mapProjectStatusToDTO } from './project-status.mapper';

/**
 * Maps ShortTeamMemberResponse to Manager domain model.
 */
const mapManagerFromDTO = (dto: OpsiumUserShortResponse): Manager => {
  return {
    id: dto.id,
    email: dto.email,
    name: dto.name,
    profileImageUrl: dto.profileImageUrl,
    isDeleted: dto.isDeleted,
  };
};

/**
 * Removes "custom:" prefix from industry values.
 * Custom industries are prefixed with "custom:" in the UI to differentiate them from API-sourced industries.
 * This prefix must be removed before sending to the backend.
 */
export const stripCustomPrefixFromIndustries = (industries?: string[]): string[] | undefined => {
  if (!industries) return undefined;
  return industries.map(industry => industry.replace(/^custom:/, ''));
};

/**
 * Maps project info response DTO to domain model.
 * Converts probability from 0-1 range (API) to 0-100 range (UI).
 * Adds "#" prefix to colorHex.
 * Industries are stored as raw values — the "custom:" prefix is only used
 * by IndustrySelect for newly created industries via the MultiSelect UI.
 */
export const mapProjectInfoFromResponse = (response: ProjectInfoResponse): ProjectInfo => {
  return {
    colorHex: normalizeColorHex(response.colorHex) ?? response.colorHex,
    name: response.name,
    logoUrl: response.logoUrl,
    status: mapProjectStatusFromDTO(response.status),
    tentativeProbability:
      response.tentativeProbability !== null && response.tentativeProbability !== undefined
        ? response.tentativeProbability * 100
        : response.tentativeProbability,
    archivedDate: response.archivedDate,
    managers: (response.managers ?? []).map(mapManagerFromDTO),
    projectStartDate: response.projectStartDate,
    projectEndDate: response.projectEndDate,
    industries: response.industries,
    description: response.description,
    customAttributesValues: (response.customAttributesValues ?? []).map(
      mapAttributeValueFromResponse
    ),
    client: undefined,
  };
};

/**
 * Maps ProjectFormData info fields to CreateProjectInfoChangeRequest DTO.
 * Converts probability from 0-100 range (UI) to 0-1 range (API).
 * Removes "#" prefix from colorHex.
 * Removes "custom:" prefix from industries.
 * Only includes tentativeProbability when status is TENTATIVE.
 */
export const mapProjectInfoToDTO = (
  data: ProjectFormData,
  availableAttributes: CustomAttribute[] = []
): CreateProjectInfoChangeRequest => {
  const status = data.status || ProjectStatus.Tentative;

  return {
    name: data.name,
    status: mapProjectStatusToDTO(status),
    colorHex: data.color ? data.color.replace('#', '') : undefined,
    clientId: data.clientId ? parseInt(data.clientId, 10) : undefined,
    // Only include tentativeProbability when status is TENTATIVE
    tentativeProbability:
      status === ProjectStatus.Tentative && data.probability !== undefined
        ? data.probability / 100
        : undefined,
    managerUserIds:
      data.managerIds && data.managerIds.length > 0
        ? data.managerIds.map(id => parseInt(String(id), 10))
        : undefined,
    projectStartDate: data.startDate
      ? normalizeToStartOfDayUTC(new Date(data.startDate)).toISOString()
      : undefined,
    projectEndDate: data.endDate
      ? normalizeToStartOfDayUTC(new Date(data.endDate)).toISOString()
      : undefined,
    description: data.description,
    industries: stripCustomPrefixFromIndustries(data.industryIds),
    customAttributesValues:
      data.customAttributes && Object.keys(data.customAttributes).length > 0
        ? mapCustomAttributesToDTO(data.customAttributes, availableAttributes)
        : undefined,
  };
};

/**
 * Maps partial ProjectFormData info fields to partial CreateProjectInfoChangeRequest DTO for updates.
 * Only includes fields that are defined (supports partial updates).
 * Only includes tentativeProbability when status is TENTATIVE.
 */
export const mapPartialProjectInfoToDTO = (
  data: Partial<ProjectFormData>,
  availableAttributes: CustomAttribute[] = [],
  currentStatus?: ProjectStatus
): Partial<CreateProjectInfoChangeRequest> => {
  const info: Partial<CreateProjectInfoChangeRequest> = {};

  if (data.name !== undefined) info.name = data.name;
  if (data.status !== undefined) info.status = mapProjectStatusToDTO(data.status);
  if (data.color) info.colorHex = data.color.replace('#', '');
  if (data.clientId !== undefined)
    info.clientId = data.clientId ? parseInt(data.clientId, 10) : undefined;
  // Only include tentativeProbability when status is TENTATIVE
  const effectiveStatus = data.status ?? currentStatus;
  if (data.probability !== undefined && effectiveStatus === ProjectStatus.Tentative) {
    info.tentativeProbability = data.probability / 100;
  }
  if (data.managerIds !== undefined)
    info.managerUserIds =
      data.managerIds.length > 0 ? data.managerIds.map(id => parseInt(String(id), 10)) : [];
  if (data.startDate !== undefined)
    info.projectStartDate = data.startDate
      ? normalizeToStartOfDayUTC(new Date(data.startDate)).toISOString()
      : undefined;
  if (data.endDate !== undefined)
    info.projectEndDate = data.endDate
      ? normalizeToStartOfDayUTC(new Date(data.endDate)).toISOString()
      : undefined;
  if (data.industryIds !== undefined)
    info.industries = stripCustomPrefixFromIndustries(data.industryIds);
  if (data.description !== undefined) info.description = data.description;
  if (data.customAttributes !== undefined && Object.keys(data.customAttributes).length > 0) {
    info.customAttributesValues = mapCustomAttributesToDTO(
      data.customAttributes,
      availableAttributes
    );
  }

  return info;
};
